import React, { useContext } from "react";
import { t } from "i18next";
import { orderBy } from "lodash";
import { useLocation } from "react-router";
import { buildLink } from "common/routing";
import { formatDateString } from "common/utils/dates";
import {
  GetWorkflowsForPropertyQuery,
  useGetWorkflowsForPropertyQuery,
} from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { FlexColumn, FlexRow } from "../Common/__styles__/Layout";
import { Actions } from "../Common/ActionCell";
import { EmptyState } from "../Common/EmptyState";
import { Tag } from "../Common/Tag";
import { Body } from "../Common/Typography";
import { ActionsProps } from "../Inputs/DropdownMenu";
import { AddressPanelTabProps } from ".";
import { Section, Wrapper } from "./__styles__/AddressPanel";
import { AddressPanelLink } from "./__styles__/Links";
import { ListItemWrapper } from "./__styles__/Workflows";
import { AddressPanelContext } from "./AddressPanelContext";
import { Subtitle } from "./DocumentUploads/__styles__/ListItem";
import { TagsWrapper } from "./Improvements/__styles__/ListItem";

type Workflow = NonNullable<
  GetWorkflowsForPropertyQuery["property"]
>["workflows"][number];

// We don't need any props, but the parent that calls this provides props,
// so we need the signature of this to accept them.
export const Workflows = ({}: Partial<AddressPanelTabProps>) => {
  const { pathname, search } = useLocation();
  const prevLocation = `${pathname}${search}`;
  const { property, loadingProperty } = useContext(AddressPanelContext);

  const { data, loading } = useGetWorkflowsForPropertyQuery({
    variables: {
      propertyId: property?.id!,
    },
    skip: !property || loadingProperty,
  });

  let content;

  if (loading || loadingProperty) {
    content = <em>{t("common-loading")}</em>;
  } else if (data?.property?.workflows.length === 0) {
    content = (
      <EmptyState
        icon="add-file"
        title="No workflows created"
        body="Add a workflow to get started."
      />
    );
  } else {
    const sortedWorkflows =
      orderBy(data?.property?.workflows, "updatedAt", "desc") ?? [];

    content = sortedWorkflows.map(workflow => (
      <WorkflowListItem
        workflow={workflow}
        key={workflow.id}
        prevLocation={prevLocation}
      />
    ));
  }

  return (
    <Wrapper>
      <Section>
        <FlexRow style={{ justifyContent: "space-between" }}>
          <Body size="large" type="emphasis">
            Workflows
          </Body>
        </FlexRow>
        {content}
      </Section>
    </Wrapper>
  );
};

const WorkflowListItem = ({
  workflow,
  prevLocation,
}: {
  workflow: Workflow;
  prevLocation: string;
}) => {
  return (
    <ListItemWrapper>
      <FlexColumn>
        <AddressPanelLink
          to={{
            pathname: buildLink("workflow", {
              workflowId: workflow.id,
            }),
            state: { prevLocation },
          }}
        >
          <Body size="default" type="emphasis">
            {workflow.workflowDefinition.name}
          </Body>
          <Subtitle style={{ marginTop: spacing.xs.value }}>
            Last edited{" "}
            {formatDateString({
              format: "MM/DD/YYYY",
              dateString: workflow.updatedAt,
            })}
          </Subtitle>
        </AddressPanelLink>

        <TagsWrapper role="list">
          <Tag
            styleVariant={workflow.status.display}
            key={workflow.status.text}
          >
            {workflow.status.text}
          </Tag>
        </TagsWrapper>
      </FlexColumn>
      <ActionButton workflow={workflow} prevLocation={prevLocation} />
    </ListItemWrapper>
  );
};

const ActionButton = ({
  workflow,
  prevLocation,
}: {
  workflow: Workflow;
  prevLocation: string;
}) => {
  const actions: Array<ActionsProps> = [
    {
      label: "View",
      to: {
        pathname: buildLink("workflow", {
          workflowId: workflow.id,
        }),
        state: { prevLocation },
      },
    },
  ];
  return <Actions actions={actions} />;
};
